import { isNil } from '@robocorp/robocloud-shared-utils';
import createDOMPurify from 'dompurify';
import { TFunction } from 'react-i18next';

let DOMPurify: ReturnType<typeof createDOMPurify> | undefined;

const getDOMPurify = (): ReturnType<typeof createDOMPurify> => {
  if (!DOMPurify) {
    DOMPurify = createDOMPurify(typeof window !== 'undefined' ? window : undefined);
    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
      if (data.tagName === 'iframe' && node instanceof Element) {
        node.setAttribute('sandbox', 'allow-same-origin allow-scripts');
      }
      return node;
    });
  }
  return DOMPurify;
};

/**
 * Sanitize html string and remove malicious parts
 */
export const sanitizeHtml = (html: string) =>
  getDOMPurify().sanitize(html, { ADD_TAGS: ['iframe'], ADD_ATTR: ['sandbox'] });

/**
 * Split given message to array of strings at newline ('\n', ASCII 10) characters.
 *
 *  - Does not touch/care about \r (ASCII 13).
 *  - Developed for purpose of splitting console output messages from runtime for
 *    timestamping purposes. Runtime harmonizes line endings to \n.
 *
 * Note that it is not possible to determine whether the input ended with a newline or not.
 *  - Resulting array is intended to be outputted element by element with newline added after each.
 *
 * Examples:
 *   - splitToLines('Hello world') ==> ['Hello world']
 *   - splitToLines('Hello world\n') ==> ['Hello world']
 *   - splitToLines('\n\nHello\n\nworld\n\n') ==> ['', '', 'Hello', '', 'world', '']
 *
 * @param message input string
 */
export const splitToLines = (message: string): string[] => {
  if (message) {
    // split at 1..n subsequent line ending characters
    const lines = message.split('\n');

    // remove trailing empty line if one exists. result of split() will include
    // empty string as last element if message ended with '\n'. to be consistent
    // with other lines in the array we have to deal with this as a special case.
    if (lines.length && lines[lines.length - 1] === '') {
      lines.pop();
    }

    return lines;
  }
  return [];
};

export const shortenText = (text: string, length: number, suffix = '...'): string => {
  if (text.length <= length) return text;
  return `${text.slice(0, length)}${suffix}`;
};

/**
 * Extract file extension from a file path
 * @param path - target path
 */
export const getFileExt = (path: string): string => {
  const regex = /(?:\.([^.]+))?$/;
  const matches = regex.exec(path);
  return matches && matches.length === 2 ? matches[1] : '';
};

/**
 * Simple JWT Decoder to extract and parse payload
 */
export const JWTDecoder = (token: string) => {
  return JSON.parse(atob(token.split('.')[1]));
};

export const isEmptyString = (str: string | null | undefined) => {
  if (str === null || str === undefined || str.length === 0) {
    return true;
  }
  return false;
};

const specialChars = /[^a-zA-Z0-9 -]/g;
const trailingDash = /-$/g;
const spaceChars = /\s/g;
const dotChars = /\./g;
const doubleDash = /-+/g;

export const slugify = (input: string): string => {
  const result = input
    .toLowerCase()
    .replace(dotChars, '-')
    .replace(specialChars, '')
    .replace(trailingDash, '')
    .trim()
    .replace(spaceChars, '-')
    .replace(doubleDash, '-');
  return result;
};

export const beautify = (
  t: TFunction,
  value: unknown,
  options?: {
    capitalizeFirstLetterOnly: true;
  },
): string => {
  if (isNil(value)) {
    return '';
  }

  if (typeof value !== 'string') {
    return JSON.stringify(value);
  }

  if (value === '') {
    return `[${t('noValueSpecified')}]`;
  }

  const isUppercase = value === value.toUpperCase();

  if (isUppercase) {
    return value;
  }

  // Split the input string based on underscores, camelCase, or snake_case
  const splitInParts = value.split(/_|(?=[A-Z])|(?<=[a-z])_+/);

  const formattedParts = splitInParts.map((part, idx) =>
    options?.capitalizeFirstLetterOnly === true && idx > 0
      ? part.toLowerCase()
      : part.charAt(0).toUpperCase() + part.slice(1),
  );

  return formattedParts.join(' ');
};

export const translationWithFallback = (
  t: TFunction,
  translationKey: string,
  valueToTranslate: string,
) => {
  const translation = t(translationKey);

  if (translation === translationKey) {
    return beautify(t, valueToTranslate, { capitalizeFirstLetterOnly: true });
  }

  return translation;
};
